import { useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

import { LoginFields } from '@mopla/constants';
import { UserRoleEnum, WhoAmIView } from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';

import { useBusinessLayer } from '../business-logic';
import { linkPendingCredential } from '../services/socialAuth';

/**
 * Shape of the email/password login form payload.
 * Structurally identical to the form types declared in `@mopla/ui`, kept here
 * so business logic does not depend on the presentation layer.
 */
export interface ILoginFormInput {
	[LoginFields.email]: string;
	[LoginFields.password]: string;
}

export const useAuthLogin = () => {
	const navigate = useNavigate();
	const auth = getAuth();
	const { t } = useTranslation('validation');
	const { api } = useBusinessLayer();

	const [authError, setAuthError] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const showEmailLoginError = useCallback(() => {
		setErrorMessage(t('auth.user_not_found'));
		setAuthError(true);
	}, [t]);

	const validateUser = useCallback(async (): Promise<boolean> => {
		if (!auth.currentUser) {
			await auth.signOut();
			return false;
		}
		try {
			const whoami = await api.get<WhoAmIView>('/api/whoami');
			if (
				whoami.roles?.includes(UserRoleEnum.DRIVER) &&
				!whoami.isBlacklisted
			) {
				return true;
			} else {
				await auth.signOut();
				return false;
			}
		} catch (err) {
			Sentry.captureException(err);
			await auth.signOut();
			return false;
		}
	}, [auth, api]);

	const onLogin: SubmitHandler<ILoginFormInput> = async ({
		email,
		password,
	}) => {
		setErrorMessage(null);
		setAuthError(false);

		try {
			await signInWithEmailAndPassword(auth, email, password);

			const isValid = await validateUser();
			if (isValid) {
				await linkPendingCredential();
				navigate('/home');
				return;
			}
			showEmailLoginError();
		} catch (err) {
			Sentry.captureException(err);
			showEmailLoginError();
		}
	};

	return {
		validateUser,
		onLogin,
		errorMessage,
		authError,
	};
};
